import classNames from "classnames";
import { useCallback, useMemo } from "react";
import { ClassFilterType } from "@common/types/class";
import { useClasses } from "@api/class.api";
import {
    ClassWithChildren,
    deselectSiblingsInSameRoot,
    getDescendantIds,
} from "@helpers/class";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { Separator } from "@design-system/Separator/Separator";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { FilterPickerList } from "@components/pageHeader/filters/FilterPickerList/FilterPickerList";
import { findToggledValue } from "@components/pageHeader/filters/FilterPickerList/findToggledValue";
import { ClassAvatar } from "@components/avatar/ClassAvatar";
import styles from "./styles.module.scss";

const AUTO_HEIGHT_MAX = 400;

const classFilterSettings = [
    {
        label: "Any selected class",
        option: ClassFilterType.ANY_SELECTED,
    },
    {
        label: "Every selected class",
        option: ClassFilterType.ALL_SELECTED,
    },
    {
        label: "Exclude selected class",
        option: ClassFilterType.EXCLUDE_SELECTED,
    },
    {
        label: "No classes",
        option: ClassFilterType.NO_CLASSES,
    },
];

const toClassItems = ({
    classes,
    isDisabled,
}: {
    classes: ClassWithChildren[];
    isDisabled: boolean;
}): Array<PickerItemData<string>> =>
    classes.map((classItem) => ({
        value: classItem.id,
        label: classItem.label,
        leading: <ClassAvatar class={classItem} size="xs" aria-hidden />,
        disabled: isDisabled,
        children: toClassItems({ classes: classItem.children, isDisabled }),
    }));

interface ClassFilterOverlayProps {
    selectedClasses: string[];
    onChange: (classes: string[]) => void;
    behaviour: {
        selectedParentClassIncludesAllChildren: boolean;
        canSelectOnlyOneClassInTheSameRoot: boolean;
        bottomButtonIsAlwaysVisible?: boolean;
    };
    containerClassName?: string;
    applyButtonOnClick?: () => void;
    resetButtonOnClick?: () => void;
    classFilterType?: ClassFilterType;
    onSettingsChange?: (option: ClassFilterType) => void;
    showFilterTypeSettings?: boolean;
    useAutoHeight?: boolean;
}

/**
 * Searchable class tree with checkboxes, optionally under the class filter
 * type radios. `behaviour` decides whether picking a parent also picks its
 * descendants and whether one root may hold only one picked class.
 */
export const ClassFilterOverlay: React.FC<ClassFilterOverlayProps> = ({
    selectedClasses,
    onChange,
    behaviour: {
        selectedParentClassIncludesAllChildren,
        canSelectOnlyOneClassInTheSameRoot,
        bottomButtonIsAlwaysVisible = false,
    },
    applyButtonOnClick,
    resetButtonOnClick,
    classFilterType: settingsOption,
    onSettingsChange,
    showFilterTypeSettings,
    containerClassName,
    useAutoHeight,
}) => {
    const { classesMap, classesWithChildren } = useClasses();
    const isNoClasses = settingsOption === ClassFilterType.NO_CLASSES;

    const items = useMemo(
        () =>
            toClassItems({
                classes: classesWithChildren,
                isDisabled: isNoClasses,
            }),
        [classesWithChildren, isNoClasses],
    );

    const selectedParentIds = useMemo(
        () =>
            selectedClasses.filter(
                (classId) =>
                    (classesMap.get(classId)?.children.length ?? 0) > 0,
            ),
        [classesMap, selectedClasses],
    );

    const handleValueChange = useCallback(
        (nextClasses: string[]) => {
            const classId = findToggledValue(selectedClasses, nextClasses);
            if (classId === undefined) {
                return;
            }
            const newSelectedClasses = new Set(selectedClasses);
            const shouldAdd = !newSelectedClasses.has(classId);
            if (shouldAdd) {
                newSelectedClasses.add(classId);
            } else {
                newSelectedClasses.delete(classId);
            }
            if (selectedParentClassIncludesAllChildren) {
                getDescendantIds({ classesMap, id: classId }).forEach(
                    (descendantId) =>
                        newSelectedClasses[shouldAdd ? "add" : "delete"](
                            descendantId,
                        ),
                );
            }
            const classInstance = classesMap.get(classId);
            if (
                canSelectOnlyOneClassInTheSameRoot &&
                shouldAdd &&
                classInstance
            ) {
                const deselected = deselectSiblingsInSameRoot({
                    selectedIds: newSelectedClasses,
                    addedClassId: classInstance.id,
                    classesMap,
                });
                onChange(Array.from(deselected));
                return;
            }
            onChange(Array.from(newSelectedClasses));
        },
        [
            selectedClasses,
            onChange,
            classesMap,
            selectedParentClassIncludesAllChildren,
            canSelectOnlyOneClassInTheSameRoot,
        ],
    );

    const settings = showFilterTypeSettings ? (
        <>
            <RadioGroup<ClassFilterType>
                aria-label="Class filter type"
                value={settingsOption ?? ClassFilterType.ANY_SELECTED}
                onValueChange={(option) => onSettingsChange?.(option)}
                className={styles.filterTypeSettings}
            >
                {classFilterSettings.map(({ label, option }) => (
                    <Radio key={option} value={option} label={label} />
                ))}
            </RadioGroup>
            <Separator block className={styles.divider} />
        </>
    ) : undefined;

    return (
        <div className={classNames(styles.filter, containerClassName)}>
            <FilterPickerList
                items={items}
                value={selectedClasses}
                onValueChange={handleValueChange}
                defaultExpandedValues={selectedParentIds}
                emptyLabel="No matching classes"
                onDeselect={resetButtonOnClick ?? (() => onChange([]))}
                onApply={
                    bottomButtonIsAlwaysVisible || selectedClasses.length > 0
                        ? applyButtonOnClick
                        : undefined
                }
                header={settings}
                maxListHeight={useAutoHeight ? AUTO_HEIGHT_MAX : undefined}
            />
        </div>
    );
};
